import React, { useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { auth } from "../lib/firebase";
import { sendEmailVerification } from "firebase/auth";
import logo from "../assets/naim-logo.png";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from "../i18n";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());

  const handleResend = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) {
        setError(t('auth.noUserFound'));
        return;
      }
      await sendEmailVerification(user);
      setMessage(t('auth.verificationEmailSentShort'));
    } catch (err) {
      console.error('[VerifyEmail] Failed to send email:', err);
      // Translate known UserProvider error messages
      const msg = err?.message || '';
      const translatedError =
        msg.includes('No email user') ? t('auth.noEmailUserFound') :
          msg.includes('Auth not ready') ? t('auth.authNotReady') :
            msg || t('auth.failedToCheckVerification');
      setError(translatedError);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) {
        setError(t('auth.noUserFound'));
        return;
      }
      await user.reload();
      if (user.emailVerified) {
        setMessage(t('auth.emailVerifiedSuccess'));
        window.location.reload();
      } else {
        setError(t('auth.emailNotVerifiedYet'));
      }
    } catch (err) {
      console.error('[VerifyEmail] Error checking verification:', err);
      // Translate known UserProvider error messages
      const msg = err?.message || '';
      const translatedError =
        msg.includes('No email user') ? t('auth.noEmailUserFound') :
          msg.includes('Auth not ready') ? t('auth.authNotReady') :
            msg || t('auth.failedToCheckVerification');
      setError(translatedError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition"
        >
          <Globe size={16} className="text-naimGreen" />
          <span className="text-naimGreen font-medium text-sm">
            {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName?.slice(0, 8) || 'EN'}
          </span>
          <ChevronDown size={14} className="text-naimGreen" />
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code);
                  changeLanguage(lang.code);
                  setShowLanguageDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left flex justify-between items-center hover:bg-green-50 transition ${lang.code === selectedLanguage ? 'bg-green-50 text-naimGreen' : 'text-gray-700'}`}
              >
                <span>{lang.nativeName}</span>
                {lang.code === selectedLanguage && <Check size={16} className="text-naimGreen" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-md border border-gray-200 p-6 text-center">
        <img src={logo} alt="Naim" className="h-12 mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">{t('auth.checkYourInbox')}</h1>
        <p className="text-sm text-gray-600 mb-4">
          {t('auth.verificationLinkSent', { email: auth.currentUser?.email || t('auth.yourEmailAddress') })}
        </p>
        <p className="text-sm text-gray-600 mb-6">
          {t('auth.verificationEmailHelper')}
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-naimGreen text-white hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? t('auth.checking') : t('auth.iHaveVerified')}
          </button>

          <button
            onClick={handleResend}
            disabled={loading}
            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? t('auth.sending') : t('auth.resendVerificationEmail')}
          </button>

          <button
            onClick={() => auth.signOut().catch(() => { })}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t('auth.signOut')}
          </button>
        </div>

        {message && <p className="mt-4 text-sm text-green-600">{message}</p>}
        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
}
